import { getAuthToken } from "../utils/auth"
import axios from "axios"
import socketService from "../utils/socketService"

import { getApiUrl } from "../config/urls"

const RAW_API_BASE_URL = getApiUrl()
const API_BASE_URL = /\/api\/?$/.test(RAW_API_BASE_URL)
  ? RAW_API_BASE_URL.replace(/\/+$/, "")
  : `${RAW_API_BASE_URL.replace(/\/+$/, "")}/api`
const SOCKET_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, "")

const socketIoShouldUpgrade = () => {
  if (process.env.REACT_APP_SOCKETIO_UPGRADE === "0") return false
  if (process.env.REACT_APP_SOCKETIO_UPGRADE === "1") return true
  return !/(localhost|127\.0\.0\.1)/i.test(SOCKET_BASE_URL)
}

class ChatService {
  constructor() {
    this.socket = null
    this.listeners = {
      message: [],
      typing: [],
      conversationUpdate: [],
      error: [],
      connect: [],
      disconnect: [],
      reconnect_failed: [],
    }
    this.isConnecting = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000
    this.reconnectDelayMax = 5000
    this.reconnectTimeout = null
    this.messageQueue = []
    this.connectionPromise = null
    this.connectionResolve = null
    this.connectionReject = null
    this.currentUserId = null
  }

  // Connexion chat via le socket principal partagé (socketService).
  startWebSocket() {
    if (this.socket && socketService.isSocketConnected()) {
      return;
    }

    const handlers = {
      onNewMessage: (data) => {
        const chatId = data?.chatId || data?.conversationId
        const msg = data?.message || data
        const normalized = {
          ...msg,
          chatId,
          conversationId: chatId,
          id: msg?.id || msg?._id,
        }
        this.emitToListeners("message", { chatId, message: msg, ...normalized })
        if (chatId && msg) {
          this.emitToListeners("conversation_update", {
            conversationId: chatId,
            lastMessage: msg,
          })
        }
      },
      onMessageSent: (data) => {
        const chatId = data?.chatId || data?.conversationId
        const msg = data?.message || data
        this.emitToListeners("message", { chatId, message: msg, conversationId: chatId, ...msg })
      },
      onTyping: (data) => {
        this.emitToListeners("typing", {
          conversationId: data?.chatId || data?.conversationId,
          chatId: data?.chatId,
          userId: data?.userId,
          userRole: data?.userRole,
          isTyping: data?.isTyping !== false,
        })
      },
      onMessagesRead: (data) => {
        this.emitToListeners("messages_read", data)
      },
    }

    socketService
      .bindChatBridge(handlers)
      .then(() => {
        this.socket = socketService.getRawSocket()
        if (this.socket?.connected) {
          this.isConnectedFlag = true
          this.emitToListeners("connect")
        } else if (this.socket) {
          this.socket.once("connect", () => {
            this.isConnectedFlag = true
            this.emitToListeners("connect")
          })
        }
        this.socket?.on("connect_error", (error) => {
          console.error("[ChatService] Erreur WebSocket:", error)
          this.handleError(error)
        })
        this.socket?.on("disconnect", (reason) => {
          console.log("[ChatService] WebSocket déconnecté:", reason)
          this.isConnectedFlag = false
          this.emitToListeners("disconnect", { reason })
          if (reason !== "io client disconnect") {
            this.attemptReconnect()
          }
        })
      })
      .catch((error) => {
        console.error("[ChatService] Erreur lors de l'initialisation WebSocket:", error)
        this.handleError(error)
      })
  }

  // Ne coupe pas le socket global — retire seulement le pont chat.
  stopWebSocket() {
    socketService.releaseChatBridge()
    this.socket = null
    this.isConnectedFlag = false
  }

  async connect(token) {
    if (this.isConnectedFlag) {
      return Promise.resolve()
    }

    if (this.isConnecting) {
      return this.connectionPromise || Promise.resolve()
    }

    if (!token) {
      const error = new Error("Aucun token d'authentification fourni")
      console.error("[ChatService] Erreur de connexion:", error.message)
      return Promise.reject(error)
    }

    this.token = token
    this.isConnecting = true

    // Créer une nouvelle promesse de connexion
    this.connectionPromise = new Promise((resolve, reject) => {
      this.connectionResolve = resolve
      this.connectionReject = reject

      try {
        // Démarrer la connexion WebSocket
        this.startWebSocket()
        
        // Définir un timeout pour la connexion
        const connectionTimeout = setTimeout(() => {
          if (!this.isConnectedFlag) {
            const error = new Error("Timeout de connexion au serveur de chat")
            console.error("[ChatService] ", error.message)
            this.isConnecting = false
            reject(error)
          }
        }, 10000) // 10 secondes de timeout
        
        // Écouter l'événement de connexion réussie
        const onConnect = () => {
          clearTimeout(connectionTimeout)
          this.isConnecting = false
          resolve()
        }
        
        // S'abonner à l'événement de connexion
        this.on('connect', onConnect)
        
        // Nettoyer en cas d'erreur
        const onError = (error) => {
          clearTimeout(connectionTimeout)
          this.isConnecting = false
          this.off('connect', onConnect)
          reject(error)
        }
        
        this.on('error', onError)
      } catch (error) {
        console.error("[ChatService] Erreur lors de la configuration de la connexion:", error)
        this.isConnecting = false
        reject(error)
      }
    })

    return this.connectionPromise
  }

  handleOpen() {
    this.reconnectAttempts = 0
    this.isConnecting = false
    this.emitToListeners("connect")
    this.processMessageQueue()

    if (this.connectionResolve) this.connectionResolve()
    this.cleanupConnectionPromise()
  }

  handleError(error) {
    console.error("[ChatService] WebSocket error:", error)
    this.emitToListeners("error", error)

    // Ne pas rejeter immédiatement la promesse en cas d'erreur
    // Laisser la logique de reconnexion gérer cela

    if (!this.isConnected() && !this.isConnecting) {
      this.attemptReconnect()
    } else {
      // Si nous sommes toujours en train de nous connecter, échouer la promesse
      if (this.connectionReject) {
        this.connectionReject(error)
        this.cleanupConnectionPromise()
      }
    }
  }

  handleClose(event) {
    this.isConnecting = false
    this.emitToListeners("disconnect", { code: event.code, reason: event.reason })
    this.cleanupConnectionPromise()

    if (event.code !== 1000) {
      this.attemptReconnect()
    }
  }

  handleMessage(event) {
    try {
      let message
      try {
        message = typeof event.data === "string" ? JSON.parse(event.data) : event.data
      } catch (e) {
        console.error("Error parsing WebSocket message:", e, "Raw data:", event.data)
        return
      }


      // Normaliser le format du message
      let normalizedMessage

      // Gérer les différents formats de messages
      if (message.type === "new_message" && message.message) {
        // Format: { type: 'new_message', message: {...} }
        normalizedMessage = message.message
        this.emitToListeners("message", normalizedMessage)

        // Émettre également un événement de mise à jour de conversation
        if (normalizedMessage.conversation_id || normalizedMessage.conversationId) {
          this.emitToListeners("conversation_update", {
            conversationId: normalizedMessage.conversation_id || normalizedMessage.conversationId,
            lastMessage: normalizedMessage,
          })
        }
      } else if (message.type && this.listeners[message.type]) {
        // Gérer d'autres types de messages
        this.emitToListeners(message.type, message.data || message)
      } else if (message.conversation_id || message.conversationId) {
        // Format direct de message
        normalizedMessage = message
        this.emitToListeners("message", normalizedMessage)

        // Mise à jour de la conversation
        this.emitToListeners("conversation_update", {
          conversationId: normalizedMessage.conversation_id || normalizedMessage.conversationId,
          lastMessage: normalizedMessage,
        })
      } else {
        // Format inconnu, émettre tel quel
        this.emitToListeners("raw_message", message)
      }

      if (message.data && typeof message.data === "object") {
        normalizedMessage = { ...message.data, type: message.type || "message" }
      }

      switch (normalizedMessage.type) {
        case "typing":
          this.emitToListeners("typing", {
            conversationId: normalizedMessage.conversationId,
            userId: normalizedMessage.userId,
            isTyping: normalizedMessage.isTyping !== false,
          })
          break

        case "new_message":
        case "message":
          const messageData = normalizedMessage.message || normalizedMessage
          this.emitToListeners("message", {
            id: messageData.id,
            conversationId: messageData.conversationId,
            content: messageData.content,
            senderId: messageData.senderId,
            senderName: messageData.senderName,
            createdAt: messageData.createdAt || new Date().toISOString(),
            read: messageData.read || false,
            type: messageData.type || "text",
          })
          break

        default:
          this.emitToListeners(normalizedMessage.type || "unknown", normalizedMessage)
      }
    } catch (error) {
      console.error("[ChatService] Error handling message:", error, event && event.data)
      this.emitToListeners("error", { type: "message_parse_error", error })
    }
  }

  cleanupConnectionPromise() {
    this.connectionPromise = null
    this.connectionResolve = null
    this.connectionReject = null
  }

  attemptReconnect() {
    // Si on est déjà en train de se connecter, ne rien faire
    if (this.isConnecting) {
      return
    }

    // Vérifier le nombre maximal de tentatives
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(
        `[ChatService] Nombre maximum de tentatives de reconnexion atteint (${this.maxReconnectAttempts})`
      )
      this.emitToListeners("reconnect_failed")
      
      // Rejeter la promesse de connexion si elle existe
      if (this.connectionReject) {
        const error = new Error("Nombre maximum de tentatives de reconnexion atteint")
        error.code = "MAX_RECONNECT_ATTEMPTS"
        this.connectionReject(error)
        this.connectionReject = null
      }
      
      return
    }

    this.reconnectAttempts++
    
    // Calculer le délai avec backoff exponentiel
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.reconnectDelayMax
    )

    console.log(
      `[ChatService] Tentative de reconnexion (${this.reconnectAttempts}/${this.maxReconnectAttempts}) dans ${delay}ms...`
    )

    // Planifier la prochaine tentative
    this.reconnectTimeout = setTimeout(() => {
      if (this.token) {
        this.connect(this.token)
          .then(() => {
            console.log('[ChatService] Reconnexion réussie')
            this.reconnectAttempts = 0 // Réinitialiser le compteur en cas de succès
            this.emitToListeners("reconnect")
          })
          .catch((error) => {
            console.error("[ChatService] Échec de la reconnexion:", error.message)
            this.attemptReconnect()
          })
      } else {
        console.error('[ChatService] Impossible de se reconnecter: aucun token disponible')
        this.emitToListeners("reconnect_failed")
      }
    }, delay)
  }

  isConnected() {
    return this.isConnectedFlag === true
  }

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }
    this.listeners[event].push(callback)

    return () => {
      this.off(event, callback)
    }
  }

  off(event, callbackToRemove) {
    if (!this.listeners[event]) return

    if (callbackToRemove) {
      this.listeners[event] = this.listeners[event].filter((callback) => callback !== callbackToRemove)
    } else {
      this.listeners[event] = []
    }
  }

  emitToListeners(event, ...args) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((cb) => {
        try {
          cb(...args)
        } catch (error) {
          console.error(`[ChatService] Error in listener for '${event}':`, error)
        }
      })
    }
  }

  async processMessageQueue() {
    if (this.isConnected() && this.messageQueue.length > 0) {
      while (this.messageQueue.length > 0) {
        const message = this.messageQueue[0]
        try {
          await this.sendMessage(message.type, message.data, true)
          this.messageQueue.shift()
        } catch (error) {
          break
        }
      }
    }
  }

  async sendMessage(type, data = {}, reconnect = true) {
    if (!this.isConnected()) {
      if (reconnect) {
        this.messageQueue.push({ type, data })
        this.attemptReconnect()
      }
      return false
    }

    try {
      // Backend-main gère les événements de chat via Socket.IO.
      if (this.socket && this.isConnected()) {
        this.socket.emit(type, data)
        return { success: true }
      }
      return false
    } catch (error) {
      console.error("[ChatService] Error sending message:", error)
      throw error
    }
  }

  async getConversations() {
    try {
      // backend-main expose GET /api/chat/my-chats
      const response = await axios.get(`${API_BASE_URL}/chat/my-chats`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })

      let conversations = []

      // Gérer différents formats de réponse
      if (Array.isArray(response.data)) {
        conversations = response.data
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        conversations = response.data.data
      } else if (response.data?.success && Array.isArray(response.data.chats)) {
        conversations = response.data.chats
      } else if (response.data && typeof response.data === "object") {
        // Essayer de trouver un tableau dans la réponse
        const arrayInResponse = Object.values(response.data).find(
          (value) => Array.isArray(value) && value.length > 0 && value[0]?.id,
        )
        if (arrayInResponse) {
          conversations = arrayInResponse
        } else {
          console.warn("⚠️ [chatService] Aucun tableau de conversations trouvé dans la réponse")
          return []
        }
      } else {
        console.warn("⚠️ [chatService] Format de réponse inattendu pour les conversations:", response.data)
        return []
      }

      const formattedConversations = conversations.map((conv) => {
        const client = conv.clientId || conv.client || {}
        const mover = conv.demenageurId || conv.mover || {}
        const clientId = client.id || conv.client_id || conv.clientId || null
        const moverId = mover.id || conv.mover_id || conv.moverId || null
        const clientFirstName = client.first_name || client.firstName || conv.client_first_name || null
        const clientLastName = client.last_name || client.lastName || conv.client_last_name || null
        const moverFirstName = mover.first_name || mover.firstName || conv.mover_first_name || null
        const moverLastName = mover.last_name || mover.lastName || conv.mover_last_name || null
        const lastMessage = conv.lastMessage || conv.last_message || null

        const formatted = {
          id: conv.id || conv.conversationId,
          // nested objects (nouveau format backend-main)
          clientId: client,
          demenageurId: mover,
          // flat aliases (compat anciens composants chat web)
          client_id: clientId,
          mover_id: moverId,
          conv_client_id: clientId,
          conv_mover_id: moverId,
          client_first_name: clientFirstName,
          client_last_name: clientLastName,
          mover_first_name: moverFirstName,
          mover_last_name: moverLastName,
          client: {
            id: clientId,
            first_name: clientFirstName,
            last_name: clientLastName,
            avatar: client.profilePicture || client.profile_picture || client.avatar_url || conv.client_avatar || null,
          },
          mover: {
            id: moverId,
            first_name: moverFirstName,
            last_name: moverLastName,
            company_name: mover.company_name || conv.mover_company_name || null,
            avatar: mover.profilePicture || mover.profile_picture || mover.avatar_url || conv.mover_avatar || null,
          },
          last_message_content: conv.last_message_content || lastMessage?.content,
          last_message_at: conv.last_message_at || lastMessage?.createdAt,
          last_message_sender_id: conv.last_message_sender_id || lastMessage?.senderId || null,
          updated_at: conv.updated_at || conv.updatedAt || new Date().toISOString(),
          unread_count: conv.unread_count || conv.unreadCount || 0,
          // Inclure les champs supplémentaires qui pourraient être utiles
          move_date: conv.move_date,
          quote_id: conv.quote_id,
          quote_status: conv.quote_status,
          // Conserver toutes les propriétés originales
          ...conv,
        }
        return formatted
      })

      return formattedConversations
    } catch (error) {
      console.error("[ChatService] Error fetching conversations:", error)
      throw error
    }
  }

  isValidUUID(uuid) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    return uuidRegex.test(uuid)
  }

  /**
   * Récupère les messages d'une conversation
   * @param {string} conversationId - L'identifiant de la conversation
   * @param {string|null} beforeMessageId - ID du message à partir duquel récupérer les messages plus anciens
   * @param {number} limit - Nombre maximum de messages à récupérer (par défaut: 20)
   * @returns {Promise<Array>} Tableau de messages
   */
  async getMessages(conversationId, beforeMessageId = null, limit = 20) {
    try {
      // Validation de l'ID de conversation
      if (!this.isValidUUID(conversationId)) {
        console.warn(`[ChatService] Invalid UUID format for conversationId: ${conversationId}`)

        // Essayer de récupérer une conversation valide
        const conversations = await this.getConversations()
        const validConversation = conversations.find((conv) => this.isValidUUID(conv.id))

        if (validConversation) {
          console.warn(`[ChatService] Using valid conversation ID instead: ${validConversation.id}`)
          conversationId = validConversation.id
        } else {
          console.error("[ChatService] No valid conversations available")
          return []
        }
      }

      // Préparation des paramètres de requête
      const params = new URLSearchParams()
      params.append("limit", Math.min(limit, 100)) // Limiter à 100 messages max

      if (beforeMessageId) {
        params.append("beforeMessageId", beforeMessageId)
      }


      const response = await axios.get(`${API_BASE_URL}/chat/${conversationId}/messages`, {
        params,
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        validateStatus: (status) => status >= 200 && status < 500,
      })

      // Vérification de la réponse
      if (response.status >= 400) {
        console.error(`[ChatService] Error fetching messages: ${response.status}`, {
          status: response.status,
          statusText: response.statusText,
          data: response.data,
        })
        return []
      }

      // Extraction des messages depuis différents formats de réponse
      let messages = []

      // Cas 1: La réponse a une propriété 'data' qui est un tableau (format standard du backend)
      if (response.data?.data && Array.isArray(response.data.data)) {
        messages = response.data.data
      }
      // Cas 2: La réponse est directement un tableau
      else if (Array.isArray(response.data)) {
        messages = response.data
      }
      // Cas 3: La réponse a une propriété 'messages' qui est un tableau
      else if (response.data?.messages && Array.isArray(response.data.messages)) {
        messages = response.data.messages
      }
      // Cas 4: La réponse est un objet avec une propriété 'success' et 'data' qui est un tableau
      else if (response.data?.success === true && Array.isArray(response.data.data)) {
        messages = response.data.data
      }
      // Cas 5: La réponse est un objet avec une propriété 'data' qui est un objet contenant un tableau 'rows'
      else if (response.data?.data?.rows && Array.isArray(response.data.data.rows)) {
        messages = response.data.data.rows
      }

  
      // S'assurer que nous avons un tableau valide
      if (!Array.isArray(messages)) {
        console.error("[ChatService] Could not extract messages from response:", response.data)
        return []
      }

      // Formater les messages pour avoir une structure cohérente
      return messages.map((msg) => {
        // Format de réponse du backend avec sender comme objet
        if (msg.sender && typeof msg.sender === 'object') {
          return {
            id: msg.id,
            content: msg.content || msg.message,
            sender_id: msg.sender.id || msg.sender_id,
            sender: {
              id: msg.sender.id || msg.sender_id,
              firstName: msg.sender.firstName || msg.sender.first_name || '',
              lastName: msg.sender.lastName || msg.sender.last_name || '',
              ...msg.sender
            },
            sender_name: `${msg.sender.firstName || msg.sender.first_name || ''} ${msg.sender.lastName || msg.sender.last_name || ''}`.trim() || 'Utilisateur inconnu',
            created_at: msg.createdAt || msg.created_at || new Date().toISOString(),
            is_read: msg.isRead || msg.is_read || false,
            isMine: msg.isMine || (msg.sender_id === this.currentUserId),
            ...msg // Conserver toutes les autres propriétés
          };
        }
        
        // Format avec sender_id et sender_first_name/sender_last_name (format de la requête SQL directe)
        if (msg.sender_id && (msg.sender_first_name || msg.sender_last_name)) {
          return {
            id: msg.id,
            content: msg.content || msg.message,
            sender_id: msg.sender_id,
            sender: {
              id: msg.sender_id,
              firstName: msg.sender_first_name || '',
              lastName: msg.sender_last_name || ''
            },
            sender_name: `${msg.sender_first_name || ''} ${msg.sender_last_name || ''}`.trim() || 'Utilisateur inconnu',
            created_at: msg.created_at || msg.createdAt || new Date().toISOString(),
            is_read: msg.is_read || msg.isRead || false,
            isMine: msg.sender_id === this.currentUserId,
            ...msg // Conserver toutes les autres propriétés
          };
        }
        
        // Pour les autres formats de réponse
        return {
          id: msg.id || msg.messageId,
          content: msg.content || msg.message,
          sender_id: msg.sender_id || (msg.sender ? msg.sender.id : null) || msg.senderId,
          sender: msg.sender || { id: msg.sender_id },
          sender_name: msg.sender_name || 
                     (msg.sender ? `${msg.sender.firstName || msg.sender.first_name || ''} ${msg.sender.lastName || msg.sender.last_name || ''}`.trim() : '') || 
                     'Utilisateur inconnu',
          created_at: msg.created_at || msg.createdAt || new Date().toISOString(),
          is_read: msg.is_read || msg.isRead || false,
          isMine: msg.isMine || (msg.sender_id === this.currentUserId),
          ...msg // Conserver toutes les autres propriétés
        };
      })
    } catch (error) {
      console.error("[ChatService] Error fetching messages:", {
        message: error.message,
        response: error.response?.data,
        config: error.config,
      })
      return []
    }
  }

  /**
   * Marque des messages comme lus
   * @param {string} conversationId - L'identifiant de la conversation
   * @param {Array<string>} messageIds - Tableau d'identifiants de messages à marquer comme lus
   * @returns {Promise<boolean>} true si la requête a réussi, false sinon
   */
  async markAsRead(conversationId, messageIds) {
    try {
      // Vérifier que l'ID de conversation est valide
      if (!this.isValidUUID(conversationId)) {
        console.warn(`[ChatService] Invalid conversation ID format: ${conversationId}`)
        return false
      }

      const response = await axios.put(
        `${API_BASE_URL}/chat/${conversationId}/mark-read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          validateStatus: (status) => status < 500, // Ne pas lancer d'exception pour les erreurs 4xx
        },
      )

      // Vérifier la réponse
      if (response.status === 404) {
        console.warn(`[ChatService] Conversation not found: ${conversationId}`)
        return false
      }

      if (response.status >= 400) {
        console.error(`[ChatService] Error marking messages as read: ${response.status}`, {
          status: response.status,
          statusText: response.statusText,
          data: response.data,
        })
        return false
      }

      return true
    } catch (error) {
      // Gestion des erreurs réseau ou autres erreurs
      console.error("[ChatService] Error marking messages as read:", {
        message: error.message,
        response: error.response?.data,
        config: error.config,
      })
      return false
    }
  }

  async sendChatMessage(conversationId, content) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/chat/${conversationId}/messages`,
        { content },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
        },
      )

      // Émettre un événement local pour une mise à jour immédiate
      if (response.data) {
        this.emitToListeners("message", {
          ...response.data,
          isOptimistic: false,
        })

        // Mettre à jour également la conversation
        this.emitToListeners("conversation_update", {
          conversationId,
          lastMessage: response.data,
        })
      }

      const payload = response.data?.data || response.data
      if (payload?.id) {
        this.emitToListeners("message", {
          chatId: conversationId,
          conversationId,
          message: payload,
          ...payload,
        })
      }
      return payload
    } catch (error) {
      console.error("[ChatService] Error sending message:", error.response?.data || error.message)
      throw error
    }
  }

  joinChat(chatId) {
    if (this.socket?.connected && chatId) {
      this.socket.emit("join_chat", { chatId })
    }
  }

  leaveChat(chatId) {
    if (this.socket?.connected && chatId) {
      this.socket.emit("leave_chat", { chatId })
    }
  }

  async sendTyping(conversationId, isTyping = true) {
    try {
      if (!this.isConnected() || !conversationId) return false
      this.socket.emit(isTyping ? "typing_start" : "typing_stop", {
        chatId: conversationId,
      })
      return true
    } catch (error) {
      console.error("[ChatService] Error sending typing indicator:", error)
      throw error
    }
  }

  async startConversation(participantId, message) {
    try {
      throw new Error("startConversation n'est pas supporté par backend-main. Utilisez create-from-service-request.");
    } catch (error) {
      console.error("[ChatService] Error starting conversation:", error)
      throw error
    }
  }
}

const chatService = new ChatService()
export default chatService
